import React from 'react';
import styled from '@emotion/styled';
import { css } from '@emotion/react';
import { useSelector } from 'react-redux';
import { Img } from 'react-image';
import ContentLoader from 'ui_system/utilities/ContentLoader';
import ReactTooltip from 'react-tooltip';
import PropTypes from 'prop-types';

import { getResourceTimezone } from 'data_system/resource_portal/selectors';
import { convertDateTimezone } from 'ui_system/utilities/timezoneUtils';
import { LinkButton, alignCenter, red } from 'ui_system/forms/buttons';

import defaultImage from 'views/anyguide/tours/item/images/default_image.png';
import CustomTourImage from 'views/anyguide/bookings/tour_picture/custom_tour_default.jpg';
import { Theme } from 'ui_system/theme';

import { Text, LocationSection, LocationIcon, ComputerIcon } from '../styles';

const TourInfo = styled.div`
  display: grid;
  grid-template-columns: 75px 1fr;
  grid-template-rows: 90px;
  grid-template-areas: 'picture info launchClass';
  align-items: center;
  justify-content: center;

  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    grid-template-areas: 'picture info' 'launchClass launchClass';
    grid-template-rows: 90px 1fr;
  }
`;

const PictureSection = styled.div`
  grid-area: picture;
  height: 100%;
  padding: 15px;
  padding-right: 0px;
`;

const Image = styled(Img)`
  height: 100%;
  width: 100%;
`;

const InfoSection = styled.div`
  grid-area: info;
  height: 100%;
  padding: 15px;
  padding-left: 10px;
  align-items: flex-start;
  line-height: 20px;

  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
`;

const LaunchClassSection = styled.div`
  grid-area: launchClass;
  height: 100%;
  padding: 22px 24px;
  align-items: center;
  justify-content: flex-end;
  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    border-top: 1px solid ${Theme.colors.lightLine};
  }
`;

export const launchClassButton = () => css`
  font-size: 16px;
  color: white;
  padding: 11px 35px;
  line-height: 20px;
  letter-spacing: 0px;

  @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
    width: fit-content;
    font-size: 15px;
    line-height: 19px;
    padding: 7.5px 26px;
  }
`;

const Capacity = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 90px;
  grid-template-areas: 'section section';
  border-bottom: 1px solid ${Theme.colors.lightLine};
`;

const CapacitySection = styled.div`
  border-top: 1px solid ${Theme.colors.lightLine};
  grid-area: 'section';
  padding: 15px;
  display: grid;
  align-items: center;
  justify-content: center;
  text-align: center;
`;

const SlotHeader = ({
  loading,
  slot,
  showCapacity,
  enableLaunchClassButton,
  t,
}) => {
  if (loading) return <SlotHeaderLoading />;
  const resourceTimezone = useSelector(getResourceTimezone);

  const {
    date,
    time,
    endDate,
    endTime,
    placesConfirmed,
    capacity,
    isSameDayOrLater,
    meetingUrl,
    isCustomTour,
    tour: {
      picture,
      name,
      shoppingCenter,
      placesAddress,
      isOnline,
      timeZone: tourTimeZone,
    },
  } = slot;

  const targetTimezone =
    isOnline && resourceTimezone ? resourceTimezone : tourTimeZone;
  const startDateTime = convertDateTimezone({
    originalTimezone: tourTimeZone,
    timezone: targetTimezone,
    date,
    dateFormat: 'YYYY-MM-DD',
    time,
    timeFormat: 'h:mma',
  });

  const endDateTime = convertDateTimezone({
    originalTimezone: tourTimeZone,
    timezone: targetTimezone,
    date: endDate,
    dateFormat: 'YYYY-MM-DD',
    time: endTime,
    timeFormat: 'h:mma',
  });

  const isValidMeetingUrl = meetingUrl && meetingUrl.length > 2;
  const renderLaunchClassButton =
    enableLaunchClassButton &&
    isOnline &&
    isSameDayOrLater &&
    isValidMeetingUrl;

  return (
    <React.Fragment>
      <TourInfo>
        <PictureSection>
          <Image
            src={[isCustomTour ? CustomTourImage : picture]}
            loader={<ContentLoader height="100%" width="100%" />}
            unloader={<Image src={defaultImage} />}
          />
        </PictureSection>
        <InfoSection>
          <Text bold> {name}</Text>
          <br />
          <Text>
            {startDateTime.format('ddd, MMM D, YYYY')} |{' '}
            {startDateTime.format('h:mm A')} - {endDateTime.format('h:mm A')}
          </Text>
          <br />
          <LocationSection>
            {(shoppingCenter || placesAddress) && !isOnline && <LocationIcon />}
            {(shoppingCenter || placesAddress) && !isOnline && (
              <Text
                hover
                color={Theme.colors.lightText}
                fontSize={12}
                marginLeft={16}
                data-for="locationTooltip"
                data-tip={`${shoppingCenter} ${
                  shoppingCenter && placesAddress && '<br />'
                } ${placesAddress}`}
                data-html
                data-event="click focus"
              >
                <LocationIcon />
                {shoppingCenter}
                {shoppingCenter && placesAddress ? ', ' : ''} {placesAddress}
              </Text>
            )}
            {isOnline && (
              <Text
                hover
                color={Theme.colors.lightText}
                fontSize={12}
                marginLeft={20}
                data-for="locationTooltip"
                data-tip="Online"
                data-html
                data-event="click focus"
              >
                <ComputerIcon />
                Online
              </Text>
            )}
          </LocationSection>
        </InfoSection>
        <LaunchClassSection>
          {renderLaunchClassButton && (
            <LinkButton
              target="_blank"
              href={meetingUrl}
              modifiers={[red, alignCenter, launchClassButton]}
            >
              Launch Class
            </LinkButton>
          )}
        </LaunchClassSection>
      </TourInfo>
      {showCapacity && !isCustomTour && (
        <Capacity>
          <CapacitySection>
            <Text bold>{capacity}</Text>
            <Text color={Theme.colors.lightText}>{t('TOTAL_SPOTS')}</Text>
          </CapacitySection>
          <CapacitySection>
            <Text bold>{placesConfirmed}</Text>
            <Text color={Theme.colors.lightText}>{t('CONFIRMED')}</Text>
          </CapacitySection>
        </Capacity>
      )}
      <LocationTooltip />
    </React.Fragment>
  );
};

SlotHeader.defaultProps = {
  loading: false,
  slot: {},
  showCapacity: true,
  enableLaunchClassButton: false,
  isCustomTour: false,
};

SlotHeader.propTypes = {
  slot: PropTypes.object.isRequired,
  loading: PropTypes.bool,
  showCapacity: PropTypes.bool,
  enableLaunchClassButton: PropTypes.bool,
  isCustomTour: PropTypes.bool,
};

const LocationTooltip = () => {
  return (
    <ReactTooltip
      globalEventOff="click"
      id="locationTooltip"
      place="bottom"
      effect="solid"
      css={css`
        max-width: 90vw;
        border-radius: 0;
        @media (max-width: ${Theme.mobile.phoneWidth - 1}px) {
          left: 5vw !important;
        }
      `}
      getContent={(dataTip) => `${dataTip}`}
      html
    />
  );
};

const SlotHeaderLoading = () => (
  <React.Fragment>
    <TourInfo>
      <PictureSection>
        <ContentLoader height="100%" width="100%" />
      </PictureSection>
      <InfoSection>
        <ContentLoader height={18} width={160} />
        <br />
        <ContentLoader height={18} width={200} />
        <br />
        <ContentLoader height={16} width={100} />
      </InfoSection>
    </TourInfo>
    <Capacity>
      <CapacitySection>
        <Text>
          <ContentLoader height={14} width={40} />
        </Text>
        <br />
        <ContentLoader height={22} width={90} />
      </CapacitySection>
      <CapacitySection>
        <Text>
          <ContentLoader height={14} width={40} />
        </Text>
        <br />
        <ContentLoader height={22} width={90} />
      </CapacitySection>
    </Capacity>
  </React.Fragment>
);

export default SlotHeader;
